import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card } from 'react-bootstrap';
import moment from 'moment';
import Swal from 'sweetalert2';
import fileDownload from 'js-file-download';

import AudienceActsList from '../acts/AudienceActsList';
import MotiveModal from './MotiveModal';
import CloneModal from './CloneModal';
import { ErrorMessage, WaitingMessage } from '../../extras/toastMessages';

import { C_YELLOW, C_RED } from '../../extras/colors';

import { ICON_INFO, ICON_VIDEO, ICON_EYE, ICON_CLONE, ICON_FOLDER, ICON_ANULATE, ICON_ACTUATION_LIST } from '../../extras/icons';
import { AGENDADA, CANCELADA, REPROGRAMADA, EFECTIVA, CONTINUIDAD, SUSPENDIDA, SOLICITUD, RECHAZADA, RECONVERTIDA } from '../../../data/audienceStates';

//Redux
import { useDispatch, useSelector } from 'react-redux';
import { anulateAudienceAction } from '../../../actions/audiencesActions';
//Axios
import clienteAxios from '../../../config/axios';
import ActuationPreview from '../work/treatment/ActuationPreview';
//Authorities
import Authorities from '../../../services/AuthoritiesService';
import AudienceService from '../../../services/AudienceService';
import { AUDIENCIAS_CLONAR, SOLICITUDES_MODIFICAR } from '../../../data/objects';
import { MSG_ERROR_SERVER, MSG_LOADING_INFORMATION, MSG_DOWNLOADING } from '../../../constants/messages';

import es from '../../../lang/es';

const AudienceCard = props => {
    //Tomo los props
    const { audience } = props;
    //Inicio el dispatch
    const dispatch = useDispatch();
    //Traigo el store
    const authUser = useSelector(state => state.auth.user);
    const authorities = useSelector(state => state.auth.authorities);
    //Inicializo el state local
    const [ hearingMinutes, setHearingMinutes ] = useState([]);
    const [ show, setShow ] = useState(false);
    const [ showActs, setShowActs ] = useState(false);
    const [ showPreview, setShowPreview ] = useState(false);
    const [ showCloneModal, setShowCloneModal ] = useState(false);
    const [ selectedAct, setSelectedAct ] = useState(null);
    const [ selectedText, setSelectedText ] = useState('');
    //Manejo la apertura de la ventana
    const handleOpen = () => {
        setShow(true);
    }
    //Manejo el cierre de ventana
    const handleClose = () => {
        setShow(false);
    }
    //Manejo el clonar
    const handleClone = () => {
        Swal.fire({
            title: 'Duplicar Audiencia',
            text: 'Se generará una nueva audiencia con los datos de la audiencia seleccionada',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: C_YELLOW,
            confirmButtonText: 'Si, Duplicar'
        }).then((result) => {
            if(result.value){
                setShowCloneModal(true);
            }
        })
    }
	//Manejo cierre del modal para clonar
	const handleCloseCloneForm = () => {
		setShowCloneModal(false);
	}
    //Manejo el anular
    const handleAnulate = () => {
        Swal.fire({
            title: 'Anular Solicitud',
            text: 'No se podrá revertir la operación',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: C_RED,
            confirmButtonText: 'Si, Anular'
        }).then((result) => {
            if(result.value){
                //Llamo al dispatch
                dispatch( anulateAudienceAction(audience) );
            }
        })
    }

    //Manejo ver actas de audiencia
    const handleActs = async () => {
        try {
            WaitingMessage(MSG_LOADING_INFORMATION);
            
            const url = new URL(
                `/v2/audiences/${audience.id}/hearing-minutes`,
                process.env.REACT_APP_BACKEND_URL
            ).toString();

            const response = await clienteAxios.get(url);
            
            Swal.close();
            const acts = response.data || [];

            //Si no hay actas
            if(acts.length === 0) {
                ErrorMessage('No se encontraron actas para la audiencia');
                return;
            }
            //Si hay una sola acta
            if(acts.length === 1){
                handleActView(acts[0]);
                return;
            }
            //Si hay más de una, muestro el listado
            setHearingMinutes(acts);
            setShowActs(true);
        } catch(error) {
            const response = error.response;
            let data = {
                data: null,
                message: MSG_ERROR_SERVER
            };
            if(response) {
                data = response.data;
            }
            Swal.close();
            ErrorMessage(data.message);
        }
    }
    //Manejo ver el texto de un acta
    const handleActView = async act => {
        if(act){
            //Hago la consulta
            try{
                //Cargo la espera
                WaitingMessage(MSG_LOADING_INFORMATION);
                //Hago la consulta
                const response = await clienteAxios.get(`/acta/ver-sae/${act.id}`);
                //Oculto la espera
                Swal.close();
                //Seteo acta
                setSelectedAct(act);
                setSelectedText(response.data);
                //Muestro ventana
                setShowPreview(true);
            }catch(error){
                const response = error.response;
                let data = {
                    data: null,
                    message: MSG_ERROR_SERVER
                };
                //Verifico si hay error
                if(response){
                    data = response.data;
                }
                //Oculto la espera
                Swal.close();
                //Mensaje de error
                ErrorMessage(data.message);
            }
        }else{
            //Muestro mensaje de error
            ErrorMessage('No se encontraron datos del registro seleccionado');
        }
    }

    //Manejo el cierre de la vista previa
    const handleClosePreview = () => {
        //Cierro la ventana
        setShowPreview(false);
        //Limpio los datos
        setSelectedAct(null);
        setSelectedText('');
    }
    //Manejo la descarga del archivo
    const handleDownloadAct = async () => {
        //Me fijo si hay acta seleccionada
        if(selectedAct){
            //Hago la consulta
            try{
                //Cargo la espera
                WaitingMessage(MSG_DOWNLOADING);
                //Hago la consulta
                const response = await clienteAxios.get(`/acta/descargar-sae/${selectedAct.id}`, {
                    responseType: 'blob'
                });
                //Oculto la espera
                Swal.close();
                //Oculto la ventana
                // handleClosePreview();
                //Genero el nombre del archivo
                let fileName = moment().format('YYYYMMDDHHmmss');
                fileName = `OGA_ACTA_${fileName}.pdf`;
                //Descargo el archivo
                fileDownload(response.data, fileName);
            }catch(error){
                const response = error.response;
                let data = {
                    data: null,
                    message: MSG_ERROR_SERVER
                };
                //Verifico si hay error
                if(response){
                    data = response.data;
                }
                //Oculto la espera
                Swal.close();
                //Mensaje de error
                ErrorMessage(data.message);
            }
        }else{
            //Muestro mensaje de error
            ErrorMessage('No se encontraron datos del registro seleccionado');
        }
    }
    //Renderizo
    return (
        <div className="col-12 mb-2">
            <Card className="card-audiencia">
                <Card.Body>
                    <div className="row">
                        <div className="col-md-3">
                            <strong>#{ audience.id }{ audience.ofiju ? ` / ${audience.ofiju.nombre}` : '' }</strong>
                            <br/>
                            <small>Solicitud:</small> { audience.fechaSolicitud ? moment(audience.fechaSolicitud).format('DD/MM/YYYY HH:mm') : '' }
                            <br/>
                            <small>Prog.:</small>&nbsp;
                            <strong>{ audience.inicio ? moment(audience.inicio).format('DD/MM/YYYY HH:mm') : '' }{ audience.fin ? ` a ${moment(audience.fin).format('HH:mm')}` : '' }</strong>
                            <br/>
                            <small>Sala</small> <strong>{ audience.sala ? audience.sala.descripcion : '' }</strong>
                        </div>
                        <div className="col-md-9">
                            <Badge variant={audience.estado.css}>
                                { audience.estado.descripcion.toUpperCase() }
                            </Badge>
                                <React.Fragment>
                                    <br/>
                                    <strong>{ audience.expediente ? audience.expediente.numero : null }</strong> { audience.caratula }
                                </React.Fragment>
                            <br/>
                            <i>
                            {
                                audience.audienciaTipoList ? (
                                    audience.audienciaTipoList.map((type, index) => (
                                        <span key={`arat${type.id}`}>
                                            {`${index > 0 ? ' | ' : ''}${type.descripcion}`}
                                        </span>
                                    ))
                                ) : null
                            }
                            </i>
                        </div>
                        <div className="col-md-12 text-right">
                        { audience.verActuaciones ? (
                            <React.Fragment>
                                <Button
                                    variant="secondary"
                                    className="btn-circle ml-2"
                                    size="sm"
                                    onClick={handleActs}
                                    title={es.SHOW_ACTUATIONS}
                                >
                                    <ICON_ACTUATION_LIST />
                                </Button>

                                <AudienceActsList
                                    show={showActs}
                                    handleClose={() => setShowActs(false)}
                                    audience={audience}
                                    acts={hearingMinutes}
                                    setSelectedAct={setSelectedAct}
                                    setSelectedText={setSelectedText}
                                    setShowPreview={setShowPreview}
                                />

                                <ActuationPreview
                                    show={showPreview}
                                    handleClose={handleClosePreview}
                                    act={selectedAct}
                                    text={selectedText}
                                    handleDownload={handleDownloadAct}
                                />
                            </React.Fragment>
                        ) : null }

                        {   audience.anular ? (
                                <Button variant="danger" className="btn-circle ml-2" size="sm" onClick={handleAnulate} title={ es.ANULATE }>
                                    <ICON_ANULATE />
                                </Button>
                        ) : null }

                        {
                            audience.estado.id >= SOLICITUD && audience.estado.id < RECHAZADA ? (
                                authorities && Authorities.hasPermission(authorities, AUDIENCIAS_CLONAR) ? (
                                    <React.Fragment>
                                        <Button variant="warning" size="sm" className="btn-circle ml-2" onClick={handleClone} title={ es.CLONE }>
                                            <ICON_CLONE />
                                        </Button> 
                                        <CloneModal
                                            show={showCloneModal}
                                            handleClose={handleCloseCloneForm}
                                            audience={audience}
                                        />
                                    </React.Fragment>
                                ) : null
                            ) : null
                        }

                        {   // ==========================================================================================
                            // Tareas #14593 ----------------------------------------------------------------------------
                            // ==========================================================================================
                            AudienceService.canOpen(audience, authUser) ? (
                                //Debo chequear que no sea privada, o q el usuario tenga permisos
                                authorities && Authorities.hasPermission(authorities, SOLICITUDES_MODIFICAR) ? (
                                    <Link 
                                        className={`btn btn-${audience.estado.id <= AGENDADA ? 'primary' : 'info'} btn-circle btn-sm ml-2`} 
                                        to={`/audiencias/${audience.id}/formulario-trabajo/informacion`}
                                        target="_blank"
                                        title={ es.OPEN }
                                    >
                                        {
                                            audience.estado.id <= AGENDADA ? (
                                                <ICON_FOLDER />
                                            ) :  (
                                                <ICON_EYE />
                                            )
                                        }
                                        
                                    </Link>
                                ) : (
                                    <Link className="btn btn-info btn-circle btn-sm ml-2" to={`/audiencias/${audience.id}/ver`} target="_blank" title={ es.VIEW }>
                                        <ICON_EYE />
                                    </Link>
                                )
                            ) : null
                            // ==========================================================================================
                            // Tareas #14593 ----------------------------------------------------------------------------
                            // ==========================================================================================
                            
                        }
                        
                        {
                            audience.estado.id === CANCELADA || audience.estado.id === RECHAZADA ? (
                                <React.Fragment>
                                    <Button variant="danger" size="sm" className="btn-circle ml-2" onClick={() => handleOpen()} title={ es.SHOW_INFO }>
                                        <ICON_INFO />
                                    </Button>

                                    <MotiveModal
                                        show={show}
                                        handleClose={handleClose}
                                        title="MOTIVO"
                                        subtitle={`Audience #${audience.id}`}
                                        audience={audience}
                                    />
                                </React.Fragment>
                            ) : null
                        }
                        {
                            //Debo chequear q no sea privada, o q tenga permisos o q sea parte
                            audience.estado.id === REPROGRAMADA || 
                            audience.estado.id === EFECTIVA || 
                            audience.estado.id === CONTINUIDAD || 
                            audience.estado.id === SUSPENDIDA ||
                            audience.estado.id === RECONVERTIDA ? (

                                // ==========================================================================================
                                // Tareas #14593 ----------------------------------------------------------------------------
                                // ==========================================================================================
                                AudienceService.canOpen(audience, authUser) ? (
                                    <Link className="btn btn-circle ml-2 btn-sm btn-dark" to={`/audiencias/${audience.id}/videos`} target="_blank" title={ es.VIDEO }>
                                        <ICON_VIDEO />
                                    </Link>
                                ) : null
                                // ==========================================================================================
                                // Tareas #14593 ----------------------------------------------------------------------------
                                // ==========================================================================================
                            
                            ) : null
                        }
                        </div>
                    </div>
                
                </Card.Body>
            </Card>
        </div>
    );
};

export default AudienceCard;